import type { GameContext } from '../core/context';
import { angleDelta, clamp, distance, type V2 } from '../core/math';
import { lanePoint, nearestEdge } from '../world/roads';
import { Layer } from '../physics/types';
import type { CityLayout } from '../world/types';

/** Entity ids from here up are police officers on foot (see police module), not civilians. */
const OFFICER_ID_BASE = 900000;

/**
 * Test/debug autopilot: drives the player's car along the road graph (left-hand lanes) or walks them
 * in a straight line, purely through the virtual input — the same controls a human uses. Used by the
 * Playwright mission playthroughs so jobs are completed by actually driving, not by teleporting.
 */

/** Point `t` (0..1 along travel) on the pavement to the left of an edge (or its centre for alleys). */
function pavementPoint(layout: CityLayout, edgeId: number, fwd: boolean, t: number): V2 {
  const e = layout.roads.edges[edgeId]!;
  const a = layout.roads.nodes[fwd ? e.from : e.to]!.pos;
  const b = layout.roads.nodes[fwd ? e.to : e.from]!.pos;
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const l = Math.hypot(dx, dz) || 1;
  const off = e.alley || e.sidewalk <= 0 ? 0 : e.width / 2 + Math.max(0.6, e.sidewalk / 2);
  return { x: a.x + dx * t + (dz / l) * off, z: a.z + dz * t + (-dx / l) * off };
}

/**
 * A* over the road graph; returns waypoints from `from` to `to` — left-hand lane points for driving,
 * pavement points (both directions allowed, one-way or not) for walking. Pure.
 */
/** Drive line on an edge: the left-hand lane, or the centre of narrow alleys (lanes there hug the walls). */
export function drivePoint(layout: CityLayout, edgeId: number, fwd: boolean, t: number): V2 {
  const e = layout.roads.edges[edgeId]!;
  if (!e.alley && e.width >= 7) return lanePoint(layout, e, fwd, 0, t);
  const a = layout.roads.nodes[fwd ? e.from : e.to]!.pos;
  const b = layout.roads.nodes[fwd ? e.to : e.from]!.pos;
  return { x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t };
}

/** Waypoint with an optional speed cap (junctions, alleys, the final approach). */
export type Waypoint = V2 & { cap?: number };

/** Junction speed (m/s) for a turn of `turn` radians: ~22 straight on, ~9 at 90°, 6 for a U-turn. */
export function junctionCap(turn: number): number {
  return clamp(22 - Math.abs(turn) * 8.3, 6, 22);
}

/** Braking the autopilot plans with (m/s²): reach a waypoint's cap by the time it gets there. */
const PLAN_BRAKE = 6;

/** Highest speed from which we can still slow to `cap` within `d` metres (kept no faster than the old linear ramp). */
function approachSpeed(cap: number, d: number, from: number): number {
  const room = Math.max(0, d - from);
  return Math.min(cap + room * 0.5, Math.sqrt(cap * cap + 2 * PLAN_BRAKE * room));
}

export function planRoute(layout: CityLayout, from: V2, to: V2, walk = false, alleyCost = 1.6, heading?: number): Waypoint[] {
  const nodes = layout.roads.nodes;
  const nearestNode = (p: V2): number => {
    let best = 0;
    let bd = Infinity;
    for (const n of nodes) {
      const d = distance(n.pos, p);
      if (d < bd) {
        bd = d;
        best = n.id;
      }
    }
    return best;
  };
  let start = nearestNode(from);
  const here = walk ? null : nearestEdge(layout, from);
  // Driving: begin at the end of our street that's in front of the bonnet, so we don't try a three-point
  // turn out of a parking space (or a U-turn mid-chase) with the law watching.
  if (heading !== undefined && !walk) {
    if (here) {
      const fx = Math.sin(heading);
      const fz = Math.cos(heading);
      const ahead = [here.edge.from, here.edge.to].filter((id) => {
        const p = nodes[id]!.pos;
        return (p.x - from.x) * fx + (p.z - from.z) * fz > 0;
      });
      if (ahead.length === 1) start = ahead[0]!;
    }
  }
  // Finish on the road nearest the target: route to the closer end of that edge, then along it.
  const ne = nearestEdge(layout, to);
  const goal = ne ? (ne.t < 0.5 ? ne.edge.from : ne.edge.to) : nearestNode(to);
  // adjacency: node -> [edgeIndex, forward, nextNode]
  const adj: [number, boolean, number][][] = nodes.map(() => []);
  for (const e of layout.roads.edges) {
    if (e.lanesForward > 0 || walk) adj[e.from]!.push([e.id, true, e.to]);
    if ((!e.oneWay && e.lanesBackward > 0) || walk) adj[e.to]!.push([e.id, false, e.from]);
  }
  // Lead-in: mid-street, first drive along our own street to the start junction and turn there. Without
  // it the first waypoint sits round the corner and the car cuts straight across the pavement into the
  // corner building (and the people on it) — the old heat-1 getaway did exactly that and got nicked.
  // Not when the target is on this street too: the final approach below already runs along it.
  const lead = here && (here.edge.from === start || here.edge.to === start) && distance(from, nodes[start]!.pos) > 10 && ne?.edge.id !== here.edge.id ? here : null;
  const search = (ban: number): Map<number, [number, number, boolean]> => {
    const g = new Map<number, number>([[start, 0]]);
    const came = new Map<number, [number, number, boolean]>(); // node -> [prevNode, edge, forward]
    const open = new Set<number>([start]);
    const h = (n: number): number => distance(nodes[n]!.pos, nodes[goal]!.pos);
    while (open.size) {
      let cur = -1;
      let bf = Infinity;
      for (const n of open) {
        const f = (g.get(n) ?? Infinity) + h(n);
        if (f < bf) {
          bf = f;
          cur = n;
        }
      }
      if (cur === goal) break;
      open.delete(cur);
      for (const [edge, fwd, next] of adj[cur]!) {
        if (cur === start && edge === ban) continue;
        const e = layout.roads.edges[edge]!;
        const cost = (g.get(cur) ?? 0) + distance(nodes[e.from]!.pos, nodes[e.to]!.pos) * (e.alley ? alleyCost : 1);
        if (cost < (g.get(next) ?? Infinity)) {
          g.set(next, cost);
          came.set(next, [cur, edge, fwd]);
          open.add(next);
        }
      }
    }
    return came;
  };
  // After a lead-in, don't come straight back down the street we just drove up (a hairpin at the
  // junction a van can't make): go on round the block. Fall back if that's the only way (dead end).
  let came = search(lead ? lead.edge.id : -1);
  if (lead && goal !== start && !came.has(goal)) came = search(-1);
  const legs: [number, boolean][] = [];
  let n = goal;
  while (n !== start && came.has(n)) {
    const [prev, edge, fwd] = came.get(n)!;
    legs.unshift([edge, fwd]);
    n = prev;
  }
  let leadFrom = 0;
  if (lead) {
    const fwd = lead.edge.to === start;
    legs.unshift([lead.edge.id, fwd]);
    leadFrom = (fwd ? lead.t : 1 - lead.t) + 4 / Math.max(1, distance(nodes[lead.edge.from]!.pos, nodes[lead.edge.to]!.pos));
  }
  const pts: Waypoint[] = [];
  for (let k = 0; k < legs.length; k++) {
    const [edgeId, fwd] = legs[k]!;
    const e = layout.roads.edges[edgeId]!;
    // Swing through the middle of each junction instead of cutting the kerb corner, at a speed that
    // suits the turn: straight on is nearly free, a right angle ~9 m/s, a hairpin crawls.
    if (k > 0 && !walk) {
      const [pe, pf] = legs[k - 1]!;
      const pEdge = layout.roads.edges[pe]!;
      const pa = nodes[pf ? pEdge.from : pEdge.to]!.pos;
      const node = nodes[fwd ? e.from : e.to]!.pos;
      const nb = nodes[fwd ? e.to : e.from]!.pos;
      const turn = Math.abs(angleDelta(Math.atan2(node.x - pa.x, node.z - pa.z), Math.atan2(nb.x - node.x, nb.z - node.z)));
      pts.push({ ...node, cap: e.alley ? 3 : junctionCap(turn) });
    }
    const len = distance(nodes[e.from]!.pos, nodes[e.to]!.pos);
    const steps = Math.max(2, Math.ceil(len / (walk ? 8 : 14)));
    for (let i = 1; i <= steps; i++) {
      const t = Math.min(walk ? 0.92 : 0.8, i / steps);
      if (k === 0 && t < leadFrom) continue;
      const p: Waypoint = walk ? pavementPoint(layout, edgeId, fwd, t) : drivePoint(layout, edgeId, fwd, t);
      if (e.alley) p.cap = 6;
      pts.push(p);
    }
  }
  if (ne) {
    const e = ne.edge;
    // Travel direction along the final edge: away from the goal node toward the projection.
    const fwd = goal === e.from;
    const tEnd = fwd ? ne.t : 1 - ne.t;
    for (let i = 1; i <= 3; i++) {
      const t = (tEnd * i) / 3;
      if (t > 0.04) pts.push({ ...(walk ? pavementPoint(layout, e.id, fwd, t) : drivePoint(layout, e.id, fwd, t)), cap: 6 });
    }
  }
  // Only head straight for the target if it's close to the kerb (doors, lockups off the road).
  const last = pts[pts.length - 1];
  // Head straight for the target if it's off the kerb but close (lockup doors, the open quay).
  if (!last || distance(last, to) < (walk ? 14 : 40)) pts.push({ x: to.x, z: to.z, cap: 4 });
  return pts;
}

export interface AutopilotState {
  active: boolean;
  mode: 'drive' | 'walk' | 'idle';
  done: boolean;
  waypoint: number;
  waypoints: number;
  stuckFor: number;
  remaining: number;
  /** Times the autopilot had to hop past an obstacle it couldn't drive around (counted, reported). */
  assists: number;
}

export class Autopilot {
  private route: Waypoint[] = [];
  private idx = 0;
  private mode: 'drive' | 'walk' | 'idle' = 'idle';
  private done = false;
  private stuckFor = 0;
  private reverseFor = 0;
  /** Seconds left of an overtake (aim right of the lane to pass a stopped obstacle). */
  private avoidFor = 0;
  private blockedFor = 0;
  /** Seconds spent hunted, rolling slowly behind something in the lane (time to overtake). */
  private crawlFor = 0;
  /** Seconds spent on the current waypoint. */
  private wpTime = 0;
  assists = 0;
  private target: V2 = { x: 0, z: 0 };
  /** Getaway mode: keep re-planning toward road nodes far from the police, preferring alleys. */
  private fleeing = false;
  private replanIn = 0;
  /** Cruise speed (m/s) on straights when nobody's after us (mission cars are fragile: calm is `cruise - 2`). */
  cruise = 12;
  /** Cruise speed on the run (heat > 0): a competent getaway driver, not a Sunday driver. */
  huntedCruise = 21;
  /** Seconds left of a swerve round a pedestrian, and which way (+1 = right of travel). */
  private pedAvoidFor = 0;
  private pedSide = 0;
  arriveRadius = 6;

  constructor(private readonly ctx: GameContext) {}

  /** Start a getaway: drive away from the police (re-planned every few seconds) until `stop()`. */
  flee(): void {
    this.fleeing = true;
    const t = this.pickFleeTarget();
    this.driveTo(t.x, t.z, true);
  }

  /**
   * A node 70–230 m away that is as far as possible from every police unit (or from where they last
   * saw us), with a bonus for alley mouths — narrow streets break line of sight.
   */
  private pickFleeTarget(): V2 {
    const layout = this.ctx.services.get('layout');
    const me = this.ctx.services.get('player').pos;
    const cops: V2[] = [];
    for (const u of this.ctx.services.tryGet('police')?.units() ?? []) {
      if (u.role !== 'patrol' && u.role !== 'leave' && u.role !== 'disabled' && Math.hypot(u.x - me.x, u.z - me.z) < 300) cops.push({ x: u.x, z: u.z });
    }
    const lastKnown = this.ctx.services.tryGet('heat')?.lastKnown;
    if (cops.length === 0 && lastKnown) cops.push(lastKnown);
    const alleyNode = new Set<number>();
    for (const e of layout.roads.edges) if (e.alley) (alleyNode.add(e.from), alleyNode.add(e.to));
    let best: V2 = me;
    let bestScore = -Infinity;
    for (const n of layout.roads.nodes) {
      const d = distance(n.pos, me);
      if (d < 70 || d > 230) continue;
      let near = 300;
      for (const c of cops) near = Math.min(near, distance(n.pos, c));
      // Don't run *through* them: penalise targets whose direction points at a cop.
      let ahead = 0;
      for (const c of cops) {
        const toC = { x: c.x - me.x, z: c.z - me.z };
        const toN = { x: n.pos.x - me.x, z: n.pos.z - me.z };
        const cos = (toC.x * toN.x + toC.z * toN.z) / ((Math.hypot(toC.x, toC.z) || 1) * (Math.hypot(toN.x, toN.z) || 1));
        if (cos > 0.6 && distance(me, c) < d) ahead += 80;
      }
      let behind = 0;
      const v = this.ctx.services.get('player').vehicle;
      if (v) {
        const f = { x: Math.sin(v.heading), z: Math.cos(v.heading) };
        if ((n.pos.x - me.x) * f.x + (n.pos.z - me.z) * f.z < 0) behind = v.speed < 6 ? 220 : 60;
      }
      const score = near + (alleyNode.has(n.id) ? 45 : 0) - ahead - behind - Math.abs(d - 140) * 0.2;
      if (score > bestScore) {
        bestScore = score;
        best = n.pos;
      }
    }
    return best;
  }

  driveTo(x: number, z: number, keepFleeing = false): void {
    if (!keepFleeing) this.fleeing = false;
    this.replanIn = 5;
    const player = this.ctx.services.get('player');
    this.target = { x, z };
    const v = player.vehicle;
    // Start from the end of our street in front of the bonnet, moving or not: from the nearest node
    // (often the one behind us) a re-plan mid-street would ask for a U-turn with the law on our tail.
    this.route = planRoute(this.ctx.services.get('layout'), player.pos, this.target, false, this.fleeing ? 0.6 : 1.6, v ? v.heading : undefined);
    // Arrive where the road ends up (the target itself may sit off the kerb).
    this.target = this.route[this.route.length - 1] ?? this.target;
    this.idx = 0;
    this.wpTime = 0;
    this.mode = 'drive';
    this.done = false;
    this.stuckFor = 0;
    this.reverseFor = 0;
  }

  /** Drive a given line (a race's driving line), corner caps and all, to its last point. */
  follow(points: readonly Waypoint[]): void {
    if (points.length === 0) return;
    this.fleeing = false;
    this.route = points.map((p) => ({ ...p }));
    this.target = { x: points[points.length - 1]!.x, z: points[points.length - 1]!.z };
    this.idx = 0;
    this.wpTime = 0;
    this.mode = 'drive';
    this.done = false;
    this.stuckFor = 0;
    this.reverseFor = 0;
  }

  walkTo(x: number, z: number): void {
    const player = this.ctx.services.get('player');
    this.target = { x, z };
    // Short hops go straight; longer walks follow the pavements.
    this.route = distance(player.pos, this.target) < 18 ? [this.target] : planRoute(this.ctx.services.get('layout'), player.pos, this.target, true);
    if (distance(this.route[this.route.length - 1]!, this.target) > 0.5) this.route.push(this.target);
    this.idx = 0;
    this.mode = 'walk';
    this.done = false;
    this.stuckFor = 0;
  }

  stop(): void {
    if (this.mode !== 'idle') this.ctx.input.clearVirtual();
    this.mode = 'idle';
    this.fleeing = false;
  }

  state(): AutopilotState {
    const p = this.ctx.services.get('player').pos;
    return {
      active: this.mode !== 'idle',
      mode: this.mode,
      done: this.done,
      waypoint: this.idx,
      waypoints: this.route.length,
      stuckFor: +this.stuckFor.toFixed(2),
      remaining: +distance(p, this.target).toFixed(1),
      assists: this.assists,
    };
  }

  /**
   * Nearest civilian in the corridor ahead of the bonnet (car width + 1 m each side, out to a stopping
   * distance), as distance along travel and lateral offset (+ = right). Police officers are skipped:
   * they're stepping out to stop you.
   */
  private pedInPath(pos: V2, heading: number, width: number, speed: number): { along: number; lat: number } | null {
    const peds = this.ctx.services.tryGet('peds');
    if (!peds) return null;
    const fx = Math.sin(heading);
    const fz = Math.cos(heading);
    const reach = 8 + Math.max(0, speed) * 1.2;
    let best: { along: number; lat: number } | null = null;
    peds.forEachNear(pos.x + fx * reach * 0.5, pos.z + fz * reach * 0.5, reach * 0.5 + 2, (px, pz, id) => {
      if (id >= OFFICER_ID_BASE) return;
      const dx = px - pos.x;
      const dz = pz - pos.z;
      const along = dx * fx + dz * fz;
      const lat = dx * Math.cos(heading) - dz * Math.sin(heading);
      if (along > 0 && along < reach && Math.abs(lat) < width / 2 + 1 && (!best || along < best.along)) best = { along, lat };
    });
    return best;
  }

  /** Called every fixed step (sets virtual input for the next one). */
  fixedUpdate(dt: number): void {
    if (this.mode === 'idle') return;
    const player = this.ctx.services.get('player');
    const input = this.ctx.input;
    const pos = player.pos;
    if (this.fleeing && this.mode === 'drive') {
      // Keep moving: re-plan away from the units every few seconds, or as soon as we arrive.
      this.replanIn -= dt;
      if (this.replanIn <= 0 || distance(pos, this.target) < 15) {
        const t = this.pickFleeTarget();
        const carry = this.assists;
        this.driveTo(t.x, t.z, true);
        this.assists = carry;
      }
    }
    if (distance(pos, this.target) < (this.mode === 'walk' ? 1.2 : this.arriveRadius)) {
      this.done = true;
      input.clearVirtual();
      if (this.mode === 'drive' && player.vehicle && player.vehicle.speed > 0.8) {
        input.setVirtual({ throttle: -1, steer: 0 });
        return;
      }
      this.mode = 'idle';
      return;
    }
    const prevIdx = this.idx;
    const walkMode = this.mode === 'walk';
    const reach = walkMode ? 1 : 7;
    while (this.idx < this.route.length - 1) {
      const cur = this.route[this.idx]!;
      const nxt = this.route[this.idx + 1]!;
      // Reached it, or already past it (beyond it along the way we came in): move on. "The way we came
      // in", not "toward the next one": at a turn the next leg runs across our lane, so sitting on the
      // inside of the bend would count as passed and we'd aim at the far leg through the corner building.
      // On foot keep "toward the next one": pavement legs have no lane to cut across, and the stricter
      // test left walks short of the car door (the missions e2e never got in).
      const prv = !walkMode && this.idx > 0 ? this.route[this.idx - 1]! : null;
      const dir = prv && distance(prv, cur) > 0.5 ? { x: cur.x - prv.x, z: cur.z - prv.z } : { x: nxt.x - cur.x, z: nxt.z - cur.z };
      const passed = dir.x * (pos.x - cur.x) + dir.z * (pos.z - cur.z) > 0;
      // Slow junctions (alley mouths) only count once we're really in them, or we cut the corner.
      const r = !walkMode && cur.cap !== undefined && cur.cap <= 4 ? 3.5 : reach;
      if (distance(pos, cur) < r || (passed && distance(pos, cur) < reach * 3)) this.idx++;
      else break;
    }
    this.wpTime = this.idx === prevIdx ? this.wpTime + dt : 0;
    const wp = this.route[this.idx]!;
    // Wedged for good (a parked lorry in the lane, a pile-up): hop to the waypoint and say so. Not online:
    // there the hop is a teleport the server refuses (`blocked` / `teleport`, a movement strike), not
    // something a player can do; the bot keeps reversing out like one.
    if (this.mode === 'drive' && this.wpTime > 14 && player.mode === 'driving' && !this.ctx.services.tryGet('net')?.online) {
      this.wpTime = 0;
      this.assists++;
      const next = this.route[Math.min(this.idx + 1, this.route.length - 1)]!;
      player.teleport(wp, Math.atan2(next.x - wp.x, next.z - wp.z));
      this.idx = Math.min(this.idx + 1, this.route.length - 1);
      return;
    }

    if (this.mode === 'walk') {
      const rig = this.ctx.services.tryGet('cameraRig');
      const up = rig ? rig.screenUp() : { x: 0, z: -1 };
      const rt = rig ? rig.screenRight() : { x: 1, z: 0 };
      const dx = wp.x - pos.x;
      const dz = wp.z - pos.z;
      const l = Math.hypot(dx, dz) || 1;
      input.setVirtual({ moveX: (dx * rt.x + dz * rt.z) / l, moveY: (dx * up.x + dz * up.z) / l, run: true });
      const speed = Math.hypot(player.velocity.x, player.velocity.z);
      this.stuckFor = speed < 0.4 ? this.stuckFor + dt : 0;
      return;
    }

    const v = player.vehicle;
    if (!v || player.mode !== 'driving') {
      input.clearVirtual();
      return;
    }
    let aimX = wp.x;
    let aimZ = wp.z;
    if (this.avoidFor > 0) {
      // Pass on the right (we drive on the left): shift the aim point across the centre line.
      this.avoidFor -= dt;
      const hx = wp.x - pos.x;
      const hz = wp.z - pos.z;
      const hl = Math.hypot(hx, hz) || 1;
      aimX += (-hz / hl) * -4;
      aimZ += (hx / hl) * -4;
    }
    const desired = Math.atan2(aimX - pos.x, aimZ - pos.z);
    let delta = angleDelta(v.heading, desired); // + = target is to the left
    const speed = v.telemetry.forwardSpeed;
    if (this.reverseFor > 0) {
      this.reverseFor -= dt;
      // Back out gently, and stop if something is right behind us.
      const b = v.body.forward();
      const tail = { x: pos.x - b.x * (v.spec.length / 2 + 0.2), z: pos.z - b.z * (v.spec.length / 2 + 0.2) };
      const behind = this.ctx.physics.raycast(tail, { x: -b.x, z: -b.z }, 3, { ignore: v.body });
      if (behind || speed < -4) {
        input.setVirtual({ throttle: speed < -0.5 ? 0.6 : 0, steer: 0, handbrake: false });
        if (behind) this.reverseFor = Math.min(this.reverseFor, 0.1);
        return;
      }
      input.setVirtual({ throttle: -0.55, steer: clamp(delta * 2, -1, 1), handbrake: false });
      return;
    }
    // Look further ahead for the corner after this one to slow down in time.
    const next = this.route[Math.min(this.idx + 1, this.route.length - 1)]!;
    const turnAhead = Math.abs(angleDelta(desired, Math.atan2(next.x - wp.x, next.z - wp.z)));
    // The corner after this waypoint only matters once we're close to it.
    const cornerWeight = clamp(1 - (distance(pos, wp) - 10) / 25, 0, 1);
    // Calm when nobody's after us (mission cars are fragile); quick on the run.
    const cruise = this.ctx.state.heat > 0 ? this.huntedCruise : this.cruise - 2;
    let want = clamp(cruise - Math.abs(delta) * 14 - turnAhead * 8 * cornerWeight, 4, cruise);
    // Respect caps on the waypoint we're heading for and the next one once it's close (brake in time).
    if (wp.cap !== undefined) want = Math.min(want, approachSpeed(wp.cap, distance(pos, wp), 8));
    if (next.cap !== undefined) want = Math.min(want, approachSpeed(next.cap, distance(pos, next), 10));
    // Don't plough into traffic: look ahead along the nose and brake for anything in the lane.
    const f = v.body.forward();
    const nose = { x: pos.x + f.x * (v.spec.length / 2 + 0.3), z: pos.z + f.z * (v.spec.length / 2 + 0.3) };
    const look = 6 + Math.max(0, speed) * 1.4;
    // Three-ray fan, bent toward where we're steering, so cars crossing a junction are seen too.
    let hit: { distance: number } | null = null;
    // On the run, officers stepping into the road are trying to stop you — don't stop for them.
    const huntedNow = this.ctx.state.heat > 0;
    for (const fan of [0, 0.22, -0.22]) {
      const a = Math.atan2(f.x, f.z) + fan + clamp(delta, -0.5, 0.5) * 0.6;
      const r = this.ctx.physics.raycast(nose, { x: Math.sin(a), z: Math.cos(a) }, fan === 0 ? look : look * 0.7, {
        mask: huntedNow ? Layer.VEHICLE : Layer.VEHICLE | Layer.PED,
        includeStatics: false,
        ignore: v.body,
      });
      if (r && (!hit || r.distance < hit.distance)) hit = r;
    }
    // People on foot (pedestrians aren't physics bodies, so the rays above can't see them): brake so we
    // could stop short, and swerve to the side they aren't on. Hitting one is a crime the law notices.
    const ped = this.pedInPath(pos, v.heading, v.spec.width, speed);
    if (ped) {
      // Calm: stop if we must. On the run: no dead stand next to a patrol for someone the swerve clears,
      // but somebody square in front of the bonnet gets the brakes — rolling into them at walking pace
      // is a police-witnessed pedHit (it took the old heat-1 getaway straight to level 2-3).
      const clears = Math.abs(ped.lat) > v.spec.width / 2;
      want = Math.min(want, Math.max(huntedNow && clears ? 3 : 0, Math.sqrt(2 * PLAN_BRAKE * Math.max(0, ped.along - 3.5))));
      if (this.pedAvoidFor <= 0) this.pedSide = ped.lat > 0 ? -1 : 1;
      this.pedAvoidFor = 0.7;
    }
    if (this.pedAvoidFor > 0) {
      this.pedAvoidFor -= dt;
      const rx = Math.cos(v.heading);
      const rz = -Math.sin(v.heading);
      aimX += rx * 2.6 * this.pedSide;
      aimZ += rz * 2.6 * this.pedSide;
      delta = angleDelta(v.heading, Math.atan2(aimX - pos.x, aimZ - pos.z));
    }
    let blocked = false;
    // Going round something: calm, we stop 2.2 m short; on the run we nose out at a crawl until it's
    // nearly touching — stopping that short just re-arms the reverse, and with a car behind as well the
    // getaway sat between two bumpers until the crew walked up.
    const tight = huntedNow && this.avoidFor > 0 ? 1.2 : 2.2;
    if (hit && (this.avoidFor <= 0 || hit.distance < tight)) {
      blocked = true;
      want = Math.min(want, Math.max(0, (hit.distance - 3.5) * 0.9));
    } else if (hit && hit.distance < 2.2) {
      // ...and it really is a crawl: nosing past a bumper this close must not turn into a shunt (into a
      // patrol car at >3 m/s that's a hitPolice crime).
      want = Math.min(want, 1.5);
    }
    this.blockedFor = blocked && speed < 0.8 ? this.blockedFor + dt : 0;
    // With the law about, sitting in a queue is how you get nicked: go round straight away.
    const hunted = huntedNow;
    if (this.blockedFor > (hunted ? 0.25 : 2.2)) {
      this.blockedFor = 0;
      this.avoidFor = 2.6;
      // Boxed in nose-first: back out and swing round it. (This used to wait 0.4 s against the plain
      // overtake's 0.25 s, so the overtake always fired first and reset the timer: the car sat nose to
      // bumper re-arming an overtake it had no room for until the crew walked up and nicked it.)
      if (hunted && hit && hit.distance < 2.2) {
        this.reverseFor = 0.9;
        this.avoidFor = 3;
      }
    }
    // Hunted and stuck crawling behind a slow car (one pulling over for the sirens, say): a getaway
    // driver pulls out and overtakes rather than tailing it at walking pace with a patrol on the bumper.
    this.crawlFor = hunted && blocked && speed >= 0.8 && speed < 8 && this.avoidFor <= 0 ? this.crawlFor + dt : 0;
    if (this.crawlFor > 0.8) {
      this.crawlFor = 0;
      this.avoidFor = 2.6;
    }
    const throttle = want < 0.5 && speed > 0.3 ? -1 : clamp((want - speed) * 0.35, -1, 1);
    input.setVirtual({ throttle, steer: clamp(-delta * 2.2, -1, 1), handbrake: false });
    // Waiting behind traffic is fine for a while; only reverse out when wedged against something.
    this.stuckFor = Math.abs(speed) < 0.6 ? this.stuckFor + dt * (blocked ? 0.3 : 1) : 0;
    // Hunted: never sit still for long — a stopped car next to a patrol is an arrest.
    if (this.stuckFor > (hunted ? 0.8 : 1.4)) {
      this.stuckFor = 0;
      this.reverseFor = 1.5;
    }
  }
}
